import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (!res.ok) {
    let text = "";
    try { text = await res.text(); } catch {}
    throw new Error(`${res.status}${text ? `: ${text}` : ""}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// GET /api/admin/users returns a bare array of user rows:
//   [{ id, email, name, isAdmin, timezone, createdAt }, ...]
export function useAdminUsers() {
  return useQuery({
    queryKey: ["/api/admin/users"],
    queryFn: () => fetchJson<any[]>("/api/admin/users"),
  });
}

export function useUserSubscription(userId: number | null) {
  return useQuery({
    queryKey: ["/api/admin/users", userId, "subscription"],
    queryFn: () => fetchJson<{ subscription: any | null }>(`/api/admin/users/${userId}/subscription`),
    enabled: !!userId,
  });
}

export function useUserLedger(userId: number | null, limit = 100) {
  return useQuery({
    queryKey: ["/api/admin/users", userId, "ledger", limit],
    queryFn: () => fetchJson<{ rows: any[] }>(`/api/admin/users/${userId}/ledger?limit=${limit}`),
    enabled: !!userId,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { email: string; password: string; name: string }) =>
      fetchJson<{ id: number; email: string; name: string }>(`/api/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users"] });
    },
  });
}

export function useAssignPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; planId: number }) =>
      fetchJson(`/api/admin/users/${args.userId}/assign-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId: args.planId }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "subscription"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "ledger"] });
    },
  });
}

export function useGrantCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; amount: number; reason: string }) =>
      fetchJson(`/api/admin/users/${args.userId}/grant-credits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: args.amount, reason: args.reason }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "subscription"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "ledger"] });
    },
  });
}

export function useRevokeCredits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; amount: number; reason: string }) =>
      fetchJson(`/api/admin/users/${args.userId}/revoke-credits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: args.amount, reason: args.reason }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "subscription"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "ledger"] });
    },
  });
}

export function useRefundUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: number; reason: string }) =>
      fetchJson(`/api/admin/users/${args.userId}/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: args.reason }),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "subscription"] });
      qc.invalidateQueries({ queryKey: ["/api/admin/users", vars.userId, "ledger"] });
    },
  });
}

// v7.54 — admin "Login as": mints a per-window impersonation token for the user.
export function useImpersonateUser() {
  return useMutation({
    mutationFn: (userId: number) =>
      fetchJson<{ token: string; expiresAt: number; user: { id: number; email: string; name: string | null } }>(
        `/api/admin/users/${userId}/impersonate`,
        { method: "POST" },
      ),
  });
}

export interface UserDeletionPreview {
  user: {id:number;email:string};
  counts: Record<string,number>;
  warnings: string[];
  blocked: boolean;
}
export interface UserDeletionJob {id:number|string;status:string;error?:string|null;stage?:string|null}
const deletionKey = (userId:number) => ["/api/admin/users",userId,"deletion"];
const postLifecycle = <T>(url:string,body?:unknown) => fetchJson<T>(url,{
  method:"POST",headers:{"Content-Type":"application/json"},
  ...(body === undefined ? {} : {body:JSON.stringify(body)}),
});
async function readLifecycle<T>(url:string):Promise<T>{
 const controller=new AbortController();const timer=setTimeout(()=>controller.abort(),45000);
 try{return await fetchJson<T>(url,{signal:controller.signal});}
 catch(error){if(controller.signal.aborted)throw new Error('Account checks are taking longer than expected. Refresh status to check again.');throw error;}
 finally{clearTimeout(timer);}
}
export const adminUserLifecycleApi = {
  preview:(id:number)=>readLifecycle<UserDeletionPreview>(`/api/admin/users/${id}/deletion-preview`),
  deletion:(id:number)=>readLifecycle<UserDeletionJob|null>(`/api/admin/users/${id}/deletion`),
  delete:(id:number,confirmEmail:string)=>postLifecycle<UserDeletionJob>(`/api/admin/users/${id}/deletion`,{confirmEmail}),
  retry:(id:number)=>postLifecycle<UserDeletionJob>(`/api/admin/users/${id}/deletion/retry`),
  suspension:(id:number,suspended:boolean)=>postLifecycle<{suspended:boolean}>(`/api/admin/users/${id}/suspension`,{suspended}),
};
export function useUserDeletionPreview(userId:number){
 return useQuery({queryKey:["/api/admin/users",userId,"deletion-preview"],queryFn:()=>adminUserLifecycleApi.preview(userId),staleTime:0,retry:false});
}
export function useUserDeletionJob(userId:number){
 const qc=useQueryClient();
 const query=useQuery({queryKey:deletionKey(userId),queryFn:()=>adminUserLifecycleApi.deletion(userId),retry:false,
  refetchInterval:query=>{if(query.state.error)return false;const job=query.state.data;return !job || !['completed','failed','partial'].includes(job.status)?2000:false;}});
 useEffect(()=>{if(query.data?.status==='completed')qc.invalidateQueries({queryKey:["/api/admin/users"]});},[query.data?.status,qc]);
 return query;
}
export function useDeleteUser(){
 const qc=useQueryClient();
 return useMutation({mutationFn:({userId,confirmEmail}:{userId:number;confirmEmail:string})=>adminUserLifecycleApi.delete(userId,confirmEmail),
  onSuccess:(job,args)=>{qc.setQueryData(deletionKey(args.userId),job);qc.invalidateQueries({queryKey:["/api/admin/users"]});}});
}
export function useRetryUserDeletion(){
 const qc=useQueryClient();
 return useMutation({mutationFn:(userId:number)=>adminUserLifecycleApi.retry(userId),
  onSuccess:(job,userId)=>{qc.setQueryData(deletionKey(userId),job);qc.invalidateQueries({queryKey:["/api/admin/users"]});}});
}
export function useSuspendUser(){
 const qc=useQueryClient();
 return useMutation({mutationFn:({userId,suspended}:{userId:number;suspended:boolean})=>adminUserLifecycleApi.suspension(userId,suspended),
  onSuccess:()=>{qc.invalidateQueries({queryKey:["/api/admin/users"]});}});
}
